import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import SmartStoryLink from "@/components/SmartStoryLink";
import {
  MagnifyingGlass, Article, LinkSimple, VideoCamera, MicrophoneStage,
  FileText, MapPin, Sparkle, ArrowRight, Camera, Question, WarningCircle,
} from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import ListenTranslateBar from "@/components/ListenTranslateBar";

// Icon map — story_type → icon
const TYPE_ICON = {
  news_link: LinkSimple,
  useful_link: LinkSimple,
  research: Article,
  government_information: FileText,
  video: VideoCamera,
  audio: MicrophoneStage,
  document: FileText,
  photo_story: Camera,
  observation: MapPin,
  personal_story: Article,
  nature_memory: Article,
  weather_report: Sparkle,
  agriculture_report: Sparkle,
  species_report: Sparkle,
  environmental_warning: WarningCircle,
  conservation_story: Sparkle,
  question: Question,
  discussion: Article,
  other: Article,
};

const TYPE_LABEL = {
  news_link: "News",
  useful_link: "Useful link",
  research: "Research",
  government_information: "Government",
  video: "Video",
  audio: "Audio",
  document: "Document",
  photo_story: "Photo story",
  observation: "Observation",
  personal_story: "Personal story",
  nature_memory: "Nature memory",
  weather_report: "Weather report",
  agriculture_report: "Agriculture",
  species_report: "Species",
  environmental_warning: "Warning",
  conservation_story: "Conservation",
  question: "Question",
  discussion: "Discussion",
  other: "Story",
};

const FILTERS = [
  { key: "", label: "All stories" },
  { key: "photo_story", label: "Photo stories" },
  { key: "observation", label: "Observations" },
  { key: "news_link", label: "News" },
  { key: "research", label: "Research" },
  { key: "video", label: "Videos" },
  { key: "nature_memory", label: "Memories" },
  { key: "question", label: "Questions" },
  { key: "environmental_warning", label: "Warnings" },
];

function StoryCard({ s }) {
  const Icon = TYPE_ICON[s.story_type] || Article;
  const cover = s.media?.find((m) => m.media_type === "image")?.url;
  const loc = [s.location?.city, s.location?.state, s.location?.country].filter(Boolean).join(", ");
  const date = s.event_date || s.publication_date || s.created_at;
  return (
    <SmartStoryLink
      story={s}
      data-testid={`story-card-${s.id}`}
      className="group rounded-2xl border border-black/10 bg-white/70 hover:bg-white hover:border-[color:var(--np-brand,#B85F2A)] transition-colors overflow-hidden flex flex-col"
    >
      {cover ? (
        <div className="aspect-[16/10] overflow-hidden bg-black/5">
          <img src={cover} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        </div>
      ) : (
        <div className="aspect-[16/6] bg-gradient-to-br from-[#FBF2E1] to-[#F5EBDC] border-b border-black/5 grid place-items-center">
          <Icon size={36} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/70" />
        </div>
      )}
      <div className="p-4 md:p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          <Icon size={11} weight="fill" />
          <span>{TYPE_LABEL[s.story_type] || "Story"}</span>
          {s.category && <span className="text-[color:var(--np-muted,#3A3830)]">· {s.category}</span>}
        </div>
        <h3 className="mt-2 font-serif-display text-lg md:text-xl leading-[1.2] tracking-tight line-clamp-3">
          {s.title || s.headline}
        </h3>
        {s.summary && (
          <p className="mt-2 text-sm text-[#3A3830] line-clamp-3">{s.summary}</p>
        )}
        <div className="mt-auto pt-3 flex items-center gap-2 text-[11px] text-[color:var(--np-muted,#3A3830)] font-mono-tech tracking-wide">
          {loc && <span className="inline-flex items-center gap-1"><MapPin size={10} weight="fill" /> {loc}</span>}
          {date && <span>· {new Date(date).toISOString().slice(0, 10)}</span>}
        </div>
        {s.publisher && s.story_type === "news_link" && (
          <div className="mt-1 text-[11px] text-[#3A3830]">— {s.publisher}</div>
        )}
      </div>
    </SmartStoryLink>
  );
}

export default function Stories() {
  const [params, setParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const type = params.get("type") || "";
  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const PAGE = 30;

  const load = useCallback(async (nextOffset = 0, append = false) => {
    setLoading(true);
    try {
      const { data } = await http.get("/nature/stories/list/all", {
        params: {
          story_type: type || undefined,
          category: category || undefined,
          country: country || undefined,
          q: q || undefined,
          limit: PAGE,
          offset: nextOffset,
        },
      });
      setStories((prev) => append ? [...prev, ...(data.stories || [])] : (data.stories || []));
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } finally { setLoading(false); }
  }, [type, category, country, q]);

  useEffect(() => { load(0, false); }, [load]);

  const setType = (t) => {
    const p = new URLSearchParams(params);
    if (t) p.set("type", t); else p.delete("type");
    setParams(p, { replace: true });
  };

  return (
    <main className="bg-[#F5EBDC] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="stories-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="NaturePulse Stories — field notes, journeys and voices from the natural world." testidPrefix="ltbar-stories" />
      </div>
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              / Stories · The living archive
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Every observation has a story.
            </h1>
            <p className="mt-2 font-serif-display text-base md:text-lg italic text-[#3A3830] max-w-2xl">
              Photos, memories, links, videos, research, questions and warnings — every form of nature knowledge, one archive.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Link
              to="/stories/new"
              data-testid="stories-cta-new"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
            >
              + Share a story
            </Link>
            <ShareButton
              title="NaturePulse — Every observation has a story"
              text="A living archive of nature stories, observations, news and community memory."
              hashtags={["NaturePulse", "CitizenScience"]}
              variant="chip"
              testid="stories-share"
            />
          </div>
        </div>

        {/* Search + filters */}
        <div className="mt-6 flex items-center gap-2 rounded-full border border-black/15 bg-white/70 backdrop-blur px-4 py-2">
          <MagnifyingGlass size={14} className="text-[color:var(--np-muted,#3A3830)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search stories, headlines, tags…"
            data-testid="stories-search"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--np-muted,#3A3830)]"
          />
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
            {total} record{total === 1 ? "" : "s"}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setType(f.key)}
              data-testid={`stories-filter-${f.key || "all"}`}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 border font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
                type === f.key
                  ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
                  : "border-black/15 text-[#1B4332] bg-white/60 hover:bg-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-white/50 border border-black/10 animate-pulse" />
            ))}
          </div>
        ) : stories.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/15 bg-white/40 p-10 text-center">
            <div className="font-serif-display text-2xl">No stories match this filter yet.</div>
            <p className="mt-2 text-sm text-[#3A3830]">
              Be the first to <Link to="/stories/new" className="underline text-[color:var(--np-brand,#B85F2A)]">share a story</Link>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stories-grid">
            {stories.map((s) => <StoryCard key={s.id} s={s} />)}
          </div>
        )}

        {!loading && stories.length > 0 && (
          <div className="mt-8 flex flex-col items-center gap-3">
            {stories.length < total && (
              <button
                onClick={() => load(offset + PAGE, true)}
                data-testid="stories-load-more"
                className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white text-[#1B4332] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-black/5"
              >
                Load more — {total - stories.length} left
              </button>
            )}
            <Link
              to="/stories/new"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase"
            >
              Add your story <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
